import { useApplications } from "@/lib/hooks/applications/useApplications";
import { useProducts } from "@/lib/hooks/products/useProducts";
import { Button, Modal } from "@ai-teach/ui-web";
import { useMutation, useQuery } from "@apollo/client/react";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import {
  SystemConfigDocument,
  TriggerSchoolSyncDocument,
  UpdateAccessControlSettingsDocument,
} from "../../../generated/graphql";

export function AccessControlSettings() {
  const { user } = useAuth();
  const isAdminOrSuperuser = ["super", "admin"].includes(user?.role || "");
  const hasMyBestUserAccess = !!user?.myBestUserId;

  const { data, loading, refetch } = useQuery(SystemConfigDocument);

  const [updateSettings] = useMutation(UpdateAccessControlSettingsDocument);
  const [triggerSync, { loading: syncLoading }] = useMutation(TriggerSchoolSyncDocument);

  const [productIds, setProductIds] = useState<string[]>([]);
  const [applicationIds, setApplicationIds] = useState<string[]>([]);
  const [productSearch, setProductSearch] = useState("");
  const [applicationSearch, setApplicationSearch] = useState("");
  const [debouncedProductSearch, setDebouncedProductSearch] = useState("");
  const [debouncedApplicationSearch, setDebouncedApplicationSearch] = useState("");
  const productSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const applicationSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    products,
    loading: productsLoading,
    loadMore: loadMoreProducts,
    hasMore: hasMoreProducts,
  } = useProducts(debouncedProductSearch);
  const {
    applications,
    loading: applicationsLoading,
    loadMore: loadMoreApplications,
    hasMore: hasMoreApplications,
  } = useApplications(debouncedApplicationSearch);
  const [isEditing, setIsEditing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string>("");
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
  const [syncStatus, setSyncStatus] = useState<string>("Initializing sync...");
  const [syncResult, setSyncResult] = useState<{
    added: number;
    updated: number;
    deactivated: number;
    total: number;
    totalSchoolsWithActiveContracts: number;
    requiredProducts: string[];
    requiredApplications: string[];
    schools: Array<{
      id: string;
      name: string;
      status: string;
      classroomCount: number;
      userCounts: {
        principal: number;
        teacher: number;
        student: number;
        secretary: number;
        loggedInPrincipal: number;
        loggedInTeacher: number;
        loggedInStudent: number;
        loggedInSecretary: number;
      };
    }>;
  } | null>(null);

  // Initialize from data
  useEffect(() => {
    if (data?.systemConfig) {
      setProductIds(data.systemConfig.requiredProductIds || []);
      setApplicationIds(data.systemConfig.requiredApplicationIds || []);

      // Load persisted sync results if they exist
      if (data.systemConfig.lastSyncResult) {
        const lastSync = data.systemConfig.lastSyncResult;
        setSyncResult({
          added: lastSync.added || 0,
          updated: lastSync.updated || 0,
          deactivated: lastSync.deactivated || 0,
          total: lastSync.total || 0,
          totalSchoolsWithActiveContracts: lastSync.totalSchoolsWithActiveContracts || 0,
          requiredProducts: lastSync.requiredProducts || [],
          requiredApplications: lastSync.requiredApplications || [],
          schools: lastSync.schools || [],
        });
      }
    }
  }, [data]);

  const handleSave = async () => {
    try {
      setSyncMessage("Saving settings and triggering sync...");
      setSyncResult(null); // Clear previous results when new sync starts
      await updateSettings({
        variables: { productIds, applicationIds },
      });
      await refetch();
      setIsEditing(false);
      setSyncMessage("Settings saved! School sync triggered automatically.");
      // Note: Sync runs in background, results will appear when complete
    } catch (error) {
      setSyncMessage(`Error: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  };

  const handleSyncNow = async () => {
    try {
      // Open modal and show initial status
      setIsSyncModalOpen(true);
      setSyncStatus("Initializing sync...");
      setSyncMessage("");
      setSyncResult(null); // Clear previous results when new sync starts

      // Update status: Fetching schools from MyBestUser
      setSyncStatus("Fetching schools from MyBestUser...");
      await new Promise((resolve) => setTimeout(resolve, 300)); // Brief delay for UX

      // Update status: Processing school data
      setSyncStatus("Processing school data and syncing classrooms...");

      const result = await triggerSync();

      if (result.data?.triggerSchoolSync) {
        setSyncStatus("Finalizing sync...");
        const syncData = result.data.triggerSchoolSync;
        setSyncResult({
          added: syncData.added || 0,
          updated: syncData.updated || 0,
          deactivated: syncData.deactivated || 0,
          total: syncData.total || 0,
          totalSchoolsWithActiveContracts: syncData.totalSchoolsWithActiveContracts || 0,
          requiredProducts: syncData.requiredProducts || [],
          requiredApplications: syncData.requiredApplications || [],
          schools: syncData.schools || [],
        });

        setSyncStatus("Sync completed successfully!");
        await new Promise((resolve) => setTimeout(resolve, 500)); // Show success message briefly

        // Refetch systemConfig to update persisted lastSyncResult
        await refetch();

        // Close modal
        setIsSyncModalOpen(false);
        setSyncMessage(""); // Clear message once results are shown
      }
    } catch (error) {
      setSyncStatus("Sync failed!");
      setSyncMessage(`Error: ${error instanceof Error ? error.message : "Unknown error"}`);
      setSyncResult(null);
      // Keep modal open to show error
      await new Promise((resolve) => setTimeout(resolve, 2000));
      setIsSyncModalOpen(false);
    }
  };

  const toggleProduct = (id: string) => {
    setProductIds((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  };

  const toggleApplication = (id: string) => {
    setApplicationIds((prev) => (prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]));
  };

  if (loading || productsLoading || applicationsLoading) return <div>Loading...</div>;

  return (
    <div className="panel p-6">
      <h3 className="h5 mb-4">Required Products & Applications</h3>
      <p className="body-sm text-gray-600 dark:text-gray-400 mb-4">
        Schools must have these products or applications in their active mybestuser contracts to
        access the platform.
      </p>

      {!hasMyBestUserAccess && isAdminOrSuperuser && (
        <div className="bg-error-500/10 border border-error-500 rounded-lg p-3 mb-4">
          <strong className="text-gray-900 dark:text-secondary-200">
            ⚠️ MyBestUser OAuth Required
          </strong>
          <p className="mt-2 caption text-error-700 dark:text-error-200">
            You need to connect your MyBestUser account via OAuth to view and manage these settings.
            Please contact an administrator to set up OAuth access.
          </p>
        </div>
      )}

      {!isAdminOrSuperuser && (
        <div className="bg-error-500/10 border border-error-500 rounded-lg p-3 mb-4 text-error-700 dark:text-error-300">
          ⚠️ Only super users and admins can view access control settings
        </div>
      )}

      {syncMessage && (
        <div
          className={`p-3 rounded-lg mb-4 ${syncMessage.includes("Error") ? "bg-error-500/10 border border-error-500 text-error-700 dark:text-error-300" : "bg-success-500/10 border border-success-500 text-success-700 dark:text-success-300"}`}
        >
          {syncMessage}
        </div>
      )}

      {/* Product IDs Multi-Select */}
      <div className="mb-6">
        <label className="block label mb-2 text-gray-900 dark:text-secondary-200">
          Required Products ({productIds.length} selected)
        </label>
        {isEditing && isAdminOrSuperuser && hasMyBestUserAccess && (
          <input
            type="text"
            placeholder="Search products..."
            value={productSearch}
            onChange={(e) => {
              setProductSearch(e.target.value);
              if (productSearchTimer.current) clearTimeout(productSearchTimer.current);
              productSearchTimer.current = setTimeout(() => {
                setDebouncedProductSearch(e.target.value);
              }, 300);
            }}
            className="w-full p-2 mb-2 border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-surface text-gray-900 dark:text-secondary-200 placeholder:text-gray-500 focus:outline-none focus:border-primary-500"
          />
        )}
        <div
          className={`max-h-[200px] overflow-y-auto border border-gray-300 dark:border-gray-800 rounded p-2 gradient-scrollbar ${
            isEditing && isAdminOrSuperuser
              ? "bg-white dark:bg-surface"
              : "bg-gray-100 dark:bg-gray-900"
          }`}
        >
          {products.length === 0 ? (
            <div className="text-gray-600 dark:text-gray-400 text-sm p-2">
              {productSearch ? "No products match your search" : "No products available"}
            </div>
          ) : (
            products.map((product) => (
              <label
                key={product.id}
                className={`flex items-center p-2 rounded mb-1 transition-colors ${
                  isEditing && isAdminOrSuperuser ? "cursor-pointer" : "cursor-default"
                } ${
                  productIds.includes(product.id)
                    ? "bg-primary-500/10 border border-primary-500/30"
                    : "bg-transparent hover:bg-gray-200 dark:hover:bg-gray-100 dark:hover:bg-gray-800/50"
                }`}
              >
                <input
                  type="checkbox"
                  checked={productIds.includes(product.id)}
                  onChange={() => toggleProduct(product.id)}
                  disabled={!isEditing || !isAdminOrSuperuser || !hasMyBestUserAccess}
                  className="mr-2"
                />
                <span className="text-sm text-gray-900 dark:text-secondary-200">
                  {product.name}
                </span>
              </label>
            ))
          )}
        </div>
        {hasMoreProducts && (
          <button
            type="button"
            onClick={loadMoreProducts}
            disabled={productsLoading}
            className="mt-1 text-xs text-primary-400 hover:text-primary-300 disabled:opacity-50"
          >
            {productsLoading ? "Loading..." : "Load more products"}
          </button>
        )}
      </div>

      {/* Application IDs Multi-Select */}
      <div className="mb-6">
        <label className="block label mb-2 text-gray-900 dark:text-secondary-200">
          Required Applications ({applicationIds.length} selected)
        </label>
        {isEditing && isAdminOrSuperuser && hasMyBestUserAccess && (
          <input
            type="text"
            placeholder="Search applications..."
            value={applicationSearch}
            onChange={(e) => {
              setApplicationSearch(e.target.value);
              if (applicationSearchTimer.current) clearTimeout(applicationSearchTimer.current);
              applicationSearchTimer.current = setTimeout(() => {
                setDebouncedApplicationSearch(e.target.value);
              }, 300);
            }}
            className="w-full p-2 mb-2 border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-surface text-gray-900 dark:text-secondary-200 placeholder:text-gray-500 focus:outline-none focus:border-primary-500"
          />
        )}
        <div
          className={`max-h-[200px] overflow-y-auto border border-gray-300 dark:border-gray-800 rounded p-2 gradient-scrollbar ${
            isEditing && isAdminOrSuperuser
              ? "bg-white dark:bg-surface"
              : "bg-gray-100 dark:bg-gray-900"
          }`}
        >
          {applications.length === 0 ? (
            <div className="text-gray-600 dark:text-gray-400 text-sm p-2">
              {applicationSearch
                ? "No applications match your search"
                : "No applications available"}
            </div>
          ) : (
            applications.map((application) => (
              <label
                key={application.id}
                className={`flex items-center p-2 rounded mb-1 transition-colors ${
                  isEditing && isAdminOrSuperuser ? "cursor-pointer" : "cursor-default"
                } ${
                  applicationIds.includes(application.id)
                    ? "bg-primary-500/10 border border-primary-500/30"
                    : "bg-transparent hover:bg-gray-200 dark:hover:bg-gray-100 dark:hover:bg-gray-800/50"
                }`}
              >
                <input
                  type="checkbox"
                  checked={applicationIds.includes(application.id)}
                  onChange={() => toggleApplication(application.id)}
                  disabled={!isEditing || !isAdminOrSuperuser || !hasMyBestUserAccess}
                  className="mr-2"
                />
                <span className="text-sm text-gray-900 dark:text-secondary-200">
                  {application.name}
                </span>
              </label>
            ))
          )}
        </div>
        {hasMoreApplications && (
          <button
            type="button"
            onClick={loadMoreApplications}
            disabled={applicationsLoading}
            className="mt-1 text-xs text-primary-400 hover:text-primary-300 disabled:opacity-50"
          >
            {applicationsLoading ? "Loading..." : "Load more applications"}
          </button>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 items-center">
        {isEditing ? (
          <>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!isAdminOrSuperuser || !hasMyBestUserAccess}
            >
              Save & Sync
            </Button>
            <Button variant="secondary" onClick={() => setIsEditing(false)}>
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="primary"
              onClick={() => setIsEditing(true)}
              disabled={!isAdminOrSuperuser || !hasMyBestUserAccess}
            >
              Edit Settings
            </Button>
            <Button
              variant="primary"
              onClick={handleSyncNow}
              disabled={!isAdminOrSuperuser || !hasMyBestUserAccess || syncLoading}
              className="ml-auto bg-success-600 hover:bg-success-700"
            >
              {syncLoading ? "Syncing..." : "Sync Now"}
            </Button>
          </>
        )}
      </div>

      {/* Info about current status */}
      <div className="mt-4 text-xs text-gray-600 dark:text-gray-400 border-t border-gray-200 dark:border-gray-800 pt-4">
        <strong>Current Settings:</strong>
        <div>• Product IDs: {productIds.length > 0 ? productIds.length : "None"}</div>
        <div>• Application IDs: {applicationIds.length > 0 ? applicationIds.length : "None"}</div>
        <div className="mt-2 italic">
          Note: Changing these settings will automatically trigger a school sync. Use "Sync Now" to
          manually sync schools with current criteria.
        </div>
      </div>

      {/* Sync Result Summary */}
      {syncResult && (
        <div className="mt-4 p-4 bg-accent-500/10 border border-accent-500 rounded-lg caption">
          {/* MyBestUser Information */}
          <div className="mb-4">
            <strong className="label text-accent-400">MyBestUser Information:</strong>
            <div className="mt-1.5 text-accent-300">
              • Number of schools with active contracts in MyBestUser:{" "}
              <strong>{syncResult.totalSchoolsWithActiveContracts}</strong>
            </div>
          </div>

          {/* Access Control Conditions */}
          <div className="mb-4 pt-3 border-t border-accent-500/30">
            <strong className="label text-accent-400">Access Control Conditions:</strong>
            <div className="mt-1.5 text-accent-300">
              <div>
                • Products:{" "}
                {syncResult.requiredProducts.length > 0
                  ? syncResult.requiredProducts.join(", ")
                  : "None"}
              </div>
              <div>
                • Applications:{" "}
                {syncResult.requiredApplications.length > 0
                  ? syncResult.requiredApplications.join(", ")
                  : "None"}
              </div>
            </div>
          </div>

          {/* Sync Summary */}
          <div className="mb-4 pt-3 border-t border-accent-500/30">
            <strong className="label text-accent-400">Sync Summary:</strong>
            <div className="mt-1.5 text-accent-300">
              • Added: {syncResult.added}
              <br />• Updated: {syncResult.updated}
              <br />• Deactivated: {syncResult.deactivated}
              <br />• <strong>Total Qualifying Schools: {syncResult.total}</strong>
            </div>
          </div>

          {/* Qualifying Schools */}
          {syncResult.schools.length > 0 && (
            <div className="pt-3 border-t border-accent-500/30">
              <strong className="label text-accent-400">Qualifying Schools:</strong>
              <div className="mt-2 max-h-[300px] overflow-y-auto gradient-scrollbar bg-gray-100 dark:bg-gray-900 border border-accent-500/20 rounded p-3">
                {syncResult.schools.map((school, index) => {
                  const totalUsers =
                    school.userCounts.principal +
                    school.userCounts.teacher +
                    school.userCounts.student +
                    school.userCounts.secretary;
                  return (
                    <div
                      key={school.id}
                      className={`${index < syncResult.schools.length - 1 ? "mb-3 pb-3 border-b border-accent-500/20" : ""}`}
                    >
                      <div className="font-semibold text-accent-300 mb-1.5">✓ {school.name}</div>
                      <div className="ml-4 text-accent-200 caption">
                        <div>• Number of classes: {school.classroomCount}</div>
                        <div>
                          • Number of users: {totalUsers}
                          <div className="ml-3 mt-0.5">
                            - Principal: {school.userCounts.principal} (total),{" "}
                            {school.userCounts.loggedInPrincipal} (logged in)
                            <br />- Teacher: {school.userCounts.teacher} (total),{" "}
                            {school.userCounts.loggedInTeacher} (logged in)
                            <br />- Student: {school.userCounts.student} (total),{" "}
                            {school.userCounts.loggedInStudent} (logged in)
                            <br />- Secretary: {school.userCounts.secretary} (total),{" "}
                            {school.userCounts.loggedInSecretary} (logged in)
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Sync Progress Modal */}
      <Modal
        isOpen={isSyncModalOpen}
        onClose={() => {
          /* Prevent closing during sync */
        }}
        title="School Sync in Progress"
        closeOnOverlayClick={false}
        showCloseButton={false}
        size="md"
      >
        <div className="text-center p-5">
          {/* Spinner */}
          <div className="mx-auto mb-5 w-[50px] h-[50px] border-4 border-gray-300 dark:border-gray-700 border-t-success-500 rounded-full animate-spin" />

          {/* Status Text */}
          <p className="text-base font-medium text-gray-900 dark:text-secondary-200 mb-2.5">
            {syncStatus}
          </p>

          {/* Error Message */}
          {syncMessage && (
            <p className="text-sm text-error-700 dark:text-error-500 mt-2.5">{syncMessage}</p>
          )}

          {/* Info */}
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-5">
            This may take a few moments...
          </p>
        </div>
      </Modal>
    </div>
  );
}
